import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import type { CustomersReport as CustomersReportData } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { RangePicker } from '../components/range-picker';
import { ReportExportButton } from '../components/report-export-button';
import { useCustomersReport } from '../hooks/use-customers-report';
import { useReportRange } from '../hooks/use-report-range';

function SplitCard({ report }: { report: CustomersReportData | undefined }) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const total = report ? report.newCustomers + report.returningCustomers : 0;
  const newShare = report && total > 0 ? report.newCustomers / total : 0;

  return (
    <Card data-tour="reports.customers.new-returning">
      <CardHeader>
        <CardTitle className="text-[15px]">{t('customers.split')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {report ? (
          <>
            <div
              className="bg-muted flex h-2.5 overflow-hidden rounded-full"
              role="img"
              aria-label={t('customers.splitSummary', {
                newShare: format.percent(newShare),
                returningShare: format.percent(total > 0 ? 1 - newShare : 0),
              })}
            >
              <div className="bg-chart-1 h-full" style={{ width: `${newShare * 100}%` }} />
              <div className="bg-chart-2 h-full flex-1" hidden={total === 0} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div role="group" aria-label={t('customers.new')} className="contents">
                <StatCard
                  label={t('customers.new')}
                  value={format.number(report.newCustomers)}
                  detail={t('customers.revenue', {
                    amount: format.money(report.newCustomerRevenue),
                  })}
                />
              </div>
              <div role="group" aria-label={t('customers.returning')} className="contents">
                <StatCard
                  label={t('customers.returning')}
                  value={format.number(report.returningCustomers)}
                  detail={t('customers.revenue', {
                    amount: format.money(report.returningCustomerRevenue),
                  })}
                />
              </div>
            </div>
          </>
        ) : (
          <Skeleton className="h-28" />
        )}
      </CardContent>
    </Card>
  );
}

function CustomersReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const { range, setRange } = useReportRange();
  const query = useCustomersReport(store.id, range);
  const report = query.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <ReportExportButton storeId={store.id} report="customers" range={range} />
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-3">
          <SplitCard report={report} />
          <Card
            className="gap-0 overflow-hidden py-0 xl:col-span-2"
            data-tour="reports.customers.top"
          >
            <CardHeader className="border-b px-5 py-4">
              <CardTitle className="text-[15px]">{t('customers.top')}</CardTitle>
              {report?.truncated ? (
                <p className="text-muted-foreground text-xs">
                  {t('topN', { count: report.limit })}
                </p>
              ) : null}
            </CardHeader>
            <Table aria-label={t('customers.top')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">{t('columns.customer')}</TableHead>
                  <TableHead className="text-right">{t('columns.orders')}</TableHead>
                  <TableHead className="pr-5 text-right">{t('columns.spent')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isPending ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={3} className="px-5">
                        <Skeleton className="h-5" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : report && report.topCustomers.length > 0 ? (
                  report.topCustomers.map((customer) => (
                    <TableRow key={customer.customerId ?? customer.email}>
                      <TableCell className="pl-5">
                        {customer.customerId ? (
                          <Link
                            to="/s/$storeId/customers/$customerId"
                            params={{ storeId: store.id, customerId: customer.customerId }}
                            className="font-medium hover:underline"
                          >
                            {customer.name ?? customer.email}
                          </Link>
                        ) : (
                          <span className="font-medium">{customer.name ?? customer.email}</span>
                        )}
                        {customer.name ? (
                          <span className="text-muted-foreground block text-xs">
                            {customer.email}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {format.number(customer.ordersCount)}
                      </TableCell>
                      <TableCell className="pr-5 text-right tabular-nums">
                        {format.money(customer.spent)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                      {t('empty.noCustomers')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}

export function CustomersReportPage() {
  return (
    <FeatureErrorBoundary name="reports.customers">
      <CustomersReport />
    </FeatureErrorBoundary>
  );
}
